import React, { useState, useEffect } from 'react';
import api from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { 
    Table, 
    Button, 
    Modal, 
    Form, 
    Badge, 
    Alert, 
    Spinner 
} from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faPlus, 
    faTrash, 
    faEnvelope, 
    faCheck, 
    faTimes, 
    faCalendarAlt,
    faInfoCircle
} from "@fortawesome/free-solid-svg-icons";

const InvitationManagement = () => {
    const [invitations, setInvitations] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { csrfToken } = useAuth();

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        community_id: '',
        make_primary: true
    });

    const fetchInvitations = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/gl_admin/invitations');
            setInvitations(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch invitations: ' + (err.response?.data?.message || err.message));
            setLoading(false);
        }
    };

    const fetchCommunities = async () => {
        try {
            const response = await api.get('/gl_admin/communities');
            setCommunities(response.data);
            if (response.data.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    community_id: response.data[0].id.toString()
                }));
            }
        } catch (err) {
        }
    };

    useEffect(() => {
        fetchInvitations();
        fetchCommunities();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setFormData({
                ...formData,
                [name]: checked
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleCreateInvitation = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await api.post('/gl_admin/invitations/create', formData, {
                headers: { 'X-CSRFToken': csrfToken }
            });
            
            setShowCreateModal(false);
            setFormData({
                email: '',
                community_id: communities.length > 0 ? communities[0].id.toString() : '',
                make_primary: true
            });
            fetchInvitations();
        } catch (err) {
            setError('Failed to create invitation: ' + (err.response?.data?.message || err.message));
            setLoading(false);
        }
    };

    const handleDeleteInvitation = async (invitationId) => {
        if (window.confirm('Are you sure you want to delete this invitation?')) {
            try {
                setLoading(true);
                await api.delete(`/gl_admin/invitations/${invitationId}`, {
                    headers: { 'X-CSRFToken': csrfToken }
                });
                fetchInvitations();
            } catch (err) {
                setError('Failed to delete invitation: ' + (err.response?.data?.message || err.message));
                setLoading(false);
            }
        }
    };

    // Get invitation status (Active, Used, Expired)
    const getInvitationStatus = (invitation) => {
        if (invitation.is_used) {
            return { status: 'Used', badge: 'success' };
        }
        
        const now = new Date();
        const expiresAt = new Date(invitation.expires_at);
        
        if (now > expiresAt) {
            return { status: 'Expired', badge: 'danger' };
        }
        
        return { status: 'Active', badge: 'primary' };
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    // Get time until expiration
    const getTimeUntilExpiration = (expiresAt) => {
        if (!expiresAt) return 'N/A';
        
        const now = new Date();
        const expiration = new Date(expiresAt);
        
        if (now > expiration) {
            return 'Expired';
        }
        
        const diffTime = Math.abs(expiration - now);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (diffDays > 0) {
            return `${diffDays} day${diffDays !== 1 ? 's' : ''} ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
        }
        
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Invitations Management</h2>
                <Button 
                    variant="primary" 
                    onClick={() => setShowCreateModal(true)}
                >
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Create Invitation
                </Button>
            </div>
            
            {error && <Alert variant="danger">{error}</Alert>}
            
            {loading && invitations.length === 0 ? (
                <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : invitations.length === 0 ? (
                <Alert variant="info">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    No invitations found. Create an invitation to get started.
                </Alert>
            ) : (
                <div className="table-responsive">
                    <Table striped hover className="align-middle">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Community</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Expires</th>
                                <th>Invited By</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invitations.map(invitation => {
                                const { status, badge } = getInvitationStatus(invitation);
                                return (
                                    <tr key={invitation.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon icon={faEnvelope} className="me-2 text-secondary" />
                                                {invitation.email}
                                            </div>
                                        </td>
                                        <td>{invitation.community_name || 'Unknown'}</td>
                                        <td>
                                            <Badge bg={badge}>{status}</Badge>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-secondary" />
                                                {formatDate(invitation.created_at)}
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                <div className="small">
                                                    {formatDate(invitation.expires_at)}
                                                </div>
                                                {status === 'Active' && (
                                                    <div className="small text-secondary">
                                                        ({getTimeUntilExpiration(invitation.expires_at)} left)
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>{invitation.invited_by_display_name || invitation.invited_by_email?.split('@')[0] || 'System'}</td>
                                        <td>
                                            <Button 
                                                variant="outline-danger" 
                                                size="sm"
                                                disabled={invitation.is_used}
                                                onClick={() => handleDeleteInvitation(invitation.id)}
                                                title={invitation.is_used ? 'Used invitations cannot be deleted' : 'Delete invitation'}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Create Invitation Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Create New Invitation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateInvitation}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter email address"
                            />
                            <Form.Text className="text-muted">
                                An invitation will be sent to this email address.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Community</Form.Label>
                            <Form.Select
                                name="community_id"
                                value={formData.community_id}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select a community</option>
                                {communities.map(community => (
                                    <option key={community.id} value={community.id}>
                                        {community.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Make primary community"
                                name="make_primary"
                                checked={formData.make_primary}
                                onChange={handleInputChange}
                            />
                            <Form.Text className="text-muted">
                                If checked, this community will be set as the user's primary community.
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={() => setShowCreateModal(false)} className="me-2">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-1"
                                        />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                                        Create & Send
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default InvitationManagement;